import { Box, Typography } from '@mui/material';
import { BirdCount } from '../components/statistics/BirdCount';
import { ObservationRate } from '../components/statistics/ObservationRate';
import { BirdHeightDistribution } from '../components/statistics/BirdHeightDistribution';
import { SpeciesData } from '../components/statistics/SpeciesData';
// import { SpeciesDistributionMonthly } from '../components/statistics/SpeciesDistributionMonthly';
import { WindDirectionRadarChart } from '../components/statistics/WindDirectionRadarChart';
import { DirectionRadarChart } from '../components/statistics/DirectionRadarChart';
import { WindSpeedWrapper } from '../components/statistics/WindSpeedWrapper';
import '../assets/styles/MultiSelect.css';
import 'react-dropdown-tree-select/dist/styles.css';
import { StaticticsStickyNavbar } from '../components/statistics/StatisticsStickyNavbar';
import { useCallback, useEffect, useState } from 'react';
import {
  GetBirdCountDirectionDateRange,
  GetBirdCountPerHour,
  GetBirdCountRange,
  GetBirdCountWindDirection,
  GetBirdHeight,
  GetBirdHeightV2,
  GetBirdHeightV3,
  GetBirdsAtCollisionRiskCountRange,
  GetCameraData,
  GetHoursRecordedPerHour,
  GetHoursRecordedRange,
  GetSiteData,
  GetSpeciesDateRange,
  GetSpeciesList,
  GetWindSpeedDateRange,
} from '../hooks/statistics.hooks';
import { GetLastProcessedDetection } from '../hooks/dashboard.hooks';
import {
  xAxisHourOptions,
  xAxisRangeOptions,
} from '../components/statistics/BarGraphOptions';
import {
  mergeByDate,
  mergeByHour,
  getStackedBarColor,
  find_bin,
  SpeciesDateRangeProcessing,
  WindSpeedDateRangeProcessing,
} from '../components/statistics/statistics.utils';
import {
  dateRangeState,
  dateValueState,
  speciesPercentageDateRangeState,
  siteState,
  cameraState,
  selectedOrdersState,
  selectedSpeciesState,
  dateRangeAdjustedState,
  speciesListState,
  speciesFilterModeState,
} from '../state/statistics.state';
import { useRecoilState } from 'recoil';
import { hoursRecordedBins } from '../data/seriesData';
import { BirdHeightDistributionV2 } from '../components/statistics/BirdHeightDistributionV2';
import { BirdHeightDistributionV3 } from '../components/statistics/BirdHeightDistributionV3';
import { BirdsAtRiskOfCollisionCount } from '../components/statistics/BirdsAtRiskOfCollisionCount';
import { useInitializeSiteAndCamera } from '../hooks/useInitializeSiteAndCamera';
import { useFeatureFlag } from '../utils/featureFlagApi';
import { useOptimalDateRange } from '../hooks/useOptimalDateRange';

export default function Statisticsv2() {
  const oneMonthBack = new Date();
  oneMonthBack.setMonth(oneMonthBack.getMonth() - 1);
  const [dateValue, setDateValue] = useRecoilState(dateValueState);
  const [site, setSite] = useRecoilState(siteState);
  const [camera, setCamera] = useRecoilState(cameraState);
  const [totalBirdsDetected, setTotalBirdsDetected] = useState(null);
  const [totalSpeciesDetected, setTotalSpeciesDetected] = useState(null);
  const [species, setSpecies] = useRecoilState(speciesListState);
  const [speciesFilterMode, setSpeciesFilterMode] = useRecoilState(speciesFilterModeState);
  const [selectedSpecies, setSelectedSpecies] = useRecoilState(selectedSpeciesState);
  const [speciesPercentageDateRange, setSpeciesPercentageDateRange] =
    useRecoilState(speciesPercentageDateRangeState);
  const [selectedOrders, setSelectedOrders] = useRecoilState(selectedOrdersState);
  const [hoursRecordedRange, setHoursRecordedRange] = useState<any | null>(
    null,
  );
  const [birdCountRange, setBirdCountRange] = useState<any | null>(null);
  const [birdsAtCollisionRiskCountRange, setBirdsAtCollisionRiskCountRange] =
    useState<any | null>(null);
  const [colorRange, setColorRange] = useState<any | null>(null);
  const [dateRange, setDateRange] = useRecoilState(dateRangeState);
  const [birdObservationRateData, setBirdObservationRateData] = useState<any[]>([]);
  const [birdCountPerHour, setBirdCountPerHour] = useState<any | null>(null);
  const [colorPerHour, setColorPerHour] = useState<any | null>(null);
  const [windSpeedCategories, setWindSpeedCategories] = useState<any | null>(
    null,
  );
  const [windSpeedValues, setWindSpeedValues] = useState<any | null>(null);
  const [hoursRecordedPerHour, setHoursRecordedPerHour] = useState<any | null>(
    null,
  );

  const [birdCountDirectionDateRange, setBirdCountDirectionDateRange] =
    useState<any | null>(null);
  const [birdCountWindDirection, setBirdCountWindDirection] = useState<
    any | null
  >(null);
  const [hasAdjustedDateRange, setHasAdjustedDateRange] = useRecoilState(dateRangeAdjustedState);

  const { data: siteData } = GetSiteData();
  const { data: cameraData } = GetCameraData(site);
  const shouldFilterSpecies =
    speciesFilterMode === 'custom' && (selectedOrders.length > 0 || selectedSpecies.length > 0);
  const filteredOrders = shouldFilterSpecies ? selectedOrders : [];
  const filteredSpecies = shouldFilterSpecies ? selectedSpecies : [];

  const { data: lastProcessedData } = GetLastProcessedDetection(site);
  const lastProcessedTimestamp = lastProcessedData?.data?.segment_timestamp ?? null;

  useOptimalDateRange({
    lastProcessedTimestamp,
    hasAdjustedDateRange,
    setHasAdjustedDateRange,
  });

  // Get company ID for the current site
  const currentSite = siteData?.data?.find((s: any) => s.id === parseInt(site));
  const companyId = currentSite?.company_id || 0;

  // Feature flags
  const { data: heightGraphV1Enabled } = useFeatureFlag(companyId, 'height_graph_v1');
  const { data: heightGraphV2Enabled } = useFeatureFlag(companyId, 'height_graph_v2');
  const { data: heightGraphV3Enabled } = useFeatureFlag(companyId, 'height_graph_v3');
  const { data: collisionRiskEnabled } = useFeatureFlag(companyId, 'collision_risk_single_cam');

  GetSpeciesList(setSpecies, dateRange, site, camera);

  const { data: hoursRecordedData, isSuccess: hoursRecordedRangeSucess, isFetching: hoursRecordedRangeFetching } =
    GetHoursRecordedRange(dateRange, site, camera);

  const { data: birdsCountData, isSuccess: birdCountRangeSuccess, isFetching: birdCountRangeFetching } =
    GetBirdCountRange(dateRange, site, camera, filteredOrders, filteredSpecies);

  const {
    data: hoursRecordedPerHourData,
    isSuccess: hoursRecordedPerHourSuccess,
    isFetching: hoursRecordedPerHourFetching,
  } = GetHoursRecordedPerHour(dateRange, site, camera);

  const { data: birdsCountPerHourData, isSuccess: birdCountPerHourSuccess, isFetching: birdCountPerHourFetching } =
    GetBirdCountPerHour(dateRange, site, camera, filteredOrders, filteredSpecies);

  const { data: speciesDateRangeData, isSuccess: speciesDateRangeSuccess } =
    GetSpeciesDateRange(dateRange, site, camera);

  const { data: windSpeedDateRangeData, isSuccess: windSpeedDateRangeSuccess } =
    GetWindSpeedDateRange(dateRange, site, camera, filteredOrders, filteredSpecies);

  const {
    data: birdCountDirectionDateRangeData,
    isSuccess: birdCountDirectionDateRangeSuccess,
  } = GetBirdCountDirectionDateRange(dateRange, site, camera, filteredOrders, filteredSpecies);

  const {
    data: birdCountWindDirectionData,
    isSuccess: birdCountWindDirectionSuccess,
  } = GetBirdCountWindDirection(dateRange, site, camera, filteredOrders, filteredSpecies);

  const { data: birdHeight, isLoading: birdHeightLoading, isError: birdHeightError } = GetBirdHeight(
    dateRange,
    site,
    camera,
    filteredOrders,
    filteredSpecies,
    heightGraphV1Enabled,
  );

  const { data: birdHeightV2, isLoading: birdHeightV2Loading, isError: birdHeightV2Error } = GetBirdHeightV2(
    dateRange,
    site,
    camera,
    filteredOrders,
    filteredSpecies,
    heightGraphV2Enabled,
  );

  const { data: birdHeightV3, isLoading: birdHeightV3Loading, isError: birdHeightV3Error } = GetBirdHeightV3(
    dateRange,
    site,
    camera,
    filteredOrders,
    filteredSpecies,
    heightGraphV3Enabled,
  );

  const {
    data: birdsAtCollisionRiskCountDataRange,
    isSuccess: birdsAtCollisionRiskCountRangeSuccess,
  } = GetBirdsAtCollisionRiskCountRange(
    dateRange,
    site,
    camera,
  )
  
  const ObservationRateProcessing = useCallback(() => {
    if (!hoursRecordedData || !birdsCountData) return;
  
    const mergedData = mergeByDate(hoursRecordedData.data, birdsCountData.data);
    const observationRateData = mergedData.map((entry: any) => ({
      date: entry.date,
      observationRate: entry.hours_recorded > 0
      ? entry.number_of_birds / entry.hours_recorded
      : 0, // Use 0 observation rate for zero recorded hours
      hoursRecorded: entry.hours_recorded,
    }));
  
    if (JSON.stringify(observationRateData) !== JSON.stringify(birdObservationRateData)) {
      setBirdObservationRateData(observationRateData); // Only update if data changes
    }
  }, [hoursRecordedData, birdsCountData, birdObservationRateData]);

  useEffect(() => {
    ObservationRateProcessing();
  }, [ObservationRateProcessing]);

  const BirdCountDataProcessing = useCallback(() => {
    if (!hoursRecordedData?.data || !birdsCountData?.data) return;
    
    const HoursWithBirdCount: any = mergeByDate(
      hoursRecordedData.data,
      birdsCountData.data,
    );
    
    if (dateRange.startDate && dateRange.endDate) {
      const existingDates = new Set(HoursWithBirdCount.map((obj: any) => obj.date));
      
      for (
        let d = new Date(dateRange.startDate.getTime());
        d <= dateRange.endDate;
        d.setDate(d.getDate() + 1)
      ) {
        const dateStr = d.toISOString().split('T')[0];
        if (!existingDates.has(dateStr)) {
          HoursWithBirdCount.push({
            date: dateStr,
            number_of_birds: 0,
            hours_recorded: 0,
          });
        }
      }
    }
    
    const totalBirds = HoursWithBirdCount.reduce(
      (accumulator: any, current: any) => accumulator + current.number_of_birds,
      0,
    );
    setTotalBirdsDetected(totalBirds);
    
    HoursWithBirdCount.sort((a: any, b: any) => a.date.localeCompare(b.date));
    
    const hoursRecordedRangeData = HoursWithBirdCount.map((obj: any) => [obj.date, 1]);
    const birdCountRangeData = HoursWithBirdCount.map((obj: any) => Object.values(obj));
    const colors = HoursWithBirdCount.map((obj: any) => getStackedBarColor(obj.hours_recorded));
    
    setHoursRecordedRange(hoursRecordedRangeData);
    setBirdCountRange(birdCountRangeData);
    setColorRange(colors);
  }, [
    birdsCountData?.data,
    dateRange.endDate,
    dateRange.startDate,
    hoursRecordedData?.data,
  ]);

  const BirdCountPerHourProcessing = useCallback(() => {
    if (!hoursRecordedPerHourData?.data || !birdsCountPerHourData?.data) return;
    
    const HoursWithBirdCount: any = mergeByHour(
      hoursRecordedPerHourData.data,
      birdsCountPerHourData.data,
    );

    // Process hours_recorded values
    HoursWithBirdCount.forEach((element: any, index: any) => {
      HoursWithBirdCount[index].hours_recorded = find_bin(
        HoursWithBirdCount[index].hours_recorded,
        dateRange,
      );
    });
    
    // Fill missing hours
    const existingHours = new Set(HoursWithBirdCount.map((obj: any) => obj.hour));
    for (let idx = 0; idx < 24; idx++) {
      if (!existingHours.has(idx)) {
        HoursWithBirdCount.push({
          hour: idx,
          number_of_birds: 0,
          hours_recorded: 0,
        });
      }
    }
    
    HoursWithBirdCount.sort((a: any, b: any) => a.hour - b.hour);
    
    const processedHoursRecordedData = HoursWithBirdCount.map((obj: any) => [obj.hour, 1]);
    const birdCountPerHourData = HoursWithBirdCount.map((obj: any) => Object.values(obj));
    const colors = HoursWithBirdCount.map((obj: any) => getStackedBarColor(obj.hours_recorded));
    
    setHoursRecordedPerHour(processedHoursRecordedData);
    setBirdCountPerHour(birdCountPerHourData);
    setColorPerHour(colors);
  }, [birdsCountPerHourData?.data, dateRange, hoursRecordedPerHourData?.data]);

  const updateCameraDetails = (event: any, newValue: any) => {
    setCamera(newValue);
  };

  useInitializeSiteAndCamera();

  useEffect(() => {
    if (birdCountRangeSuccess && hoursRecordedRangeSucess) {
      BirdCountDataProcessing();
    }
  }, [
    BirdCountDataProcessing,
    birdCountRangeSuccess,
    hoursRecordedRangeSucess,
  ]);

  useEffect(() => {
    if (birdCountPerHourSuccess && hoursRecordedPerHourSuccess) {
      BirdCountPerHourProcessing();
    }
  }, [
    hoursRecordedPerHourSuccess,
    birdCountPerHourSuccess,
    BirdCountPerHourProcessing,
  ]);

  useEffect(() => {
    if (speciesDateRangeSuccess) {
      SpeciesDateRangeProcessing(
        speciesDateRangeData,
        setSpeciesPercentageDateRange,
        setTotalSpeciesDetected,
      );
    }
  }, [
    speciesDateRangeData,
    speciesDateRangeSuccess,
    setSpeciesPercentageDateRange,
  ]);

  useEffect(() => {
    if (windSpeedDateRangeSuccess) {
      WindSpeedDateRangeProcessing(
        windSpeedDateRangeData,
        setWindSpeedCategories,
        setWindSpeedValues,
      );
    }
  }, [windSpeedDateRangeData, windSpeedDateRangeSuccess]);

  useEffect(() => {
    if (birdCountDirectionDateRangeSuccess) {
      setBirdCountDirectionDateRange(birdCountDirectionDateRangeData.data);
    }
  }, [birdCountDirectionDateRangeData, birdCountDirectionDateRangeSuccess]);

  useEffect(() => {
    if (birdsAtCollisionRiskCountRangeSuccess) {
      setBirdsAtCollisionRiskCountRange(birdsAtCollisionRiskCountDataRange.data);
    }
  }, [birdsAtCollisionRiskCountDataRange, birdsAtCollisionRiskCountRangeSuccess]);

  useEffect(() => {
    if (birdCountWindDirectionSuccess) {
      setBirdCountWindDirection(birdCountWindDirectionData.data);
    }
  }, [birdCountWindDirectionData, birdCountWindDirectionSuccess]);

  // useEffect(() => {
  //   console.log(species);
  // }, [species]);

  return (
    <Box
      component="main"
      sx={{
        backgroundColor: '#F8F9FA',
        flexGrow: 1,
        paddingTop: '50px',
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <Box sx={{ width: '25%' }}></Box>
        <Box
          sx={{
            display: 'grid',
            flexDirection: 'column',
            rowGap: '8px',
            width: '1000px',
          }}
        >
          <Typography
            sx={{
              fontSize: '32px',
              lineHeight: '40px',
              margin: 0,
              fontWeight: '600',
              color: '#17052D',
              letterSpacing: '-0.5px',
            }}
          >
            Statistics
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: '24px',
              color: '#6B7280',
              margin: '8px 0 0 0',
              fontWeight: '400',
            }}
          >
            Bird activity data collected at site
          </Typography>
          {/* <Typography
            sx={{
              fontSize: '12px',
              lineHeight: '21px',
              color: '#8B8296',
            }}
          >
            Filter the data by time and species
          </Typography> */}
        </Box>
      </Box>
      <StaticticsStickyNavbar
        dateRange={dateRange}
        setDateRange={setDateRange}
        site={site}
        setSite={setSite}
        listOfSites={siteData}
        updateCameraDetails={updateCameraDetails}
        cameraData={cameraData}
        camera={camera}
        speciesList={species}
        setSelectedOrders={setSelectedOrders}
        setSelectedSpecies={setSelectedSpecies}
        selectedOrders={selectedOrders}
        selectedSpecies={selectedSpecies}
        dateValue={dateValue}
        setDateValue={setDateValue}
        setDateRangeAdjusted={setHasAdjustedDateRange}
        setSpeciesFilterMode={setSpeciesFilterMode}
      />
      
      <Box sx={{ display: 'flex', width: '100%' }}>
        <Box sx={{ width: '25%' }}></Box>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '1000px',
          maxWidth: 'calc(100vw - 25%)',
          paddingTop: '24px',
          paddingBottom: '375px',
          gap: '32px',
          overflow: 'hidden',
        }}>
          <Box id="yearly-bird-count">
            <BirdCount
              barGraphBirdCount={birdCountRange}
              barGraphReliable={hoursRecordedRange}
              colors={colorRange}
              xAxisOptions={xAxisRangeOptions}
              hoursRecordedBins={hoursRecordedBins}
              tickAmount={10}
              type="day"
              header="Count of birds observed"
              subHeader="This graph shows the number of birds observed per day during the selected date range"
              isLoading={(!birdCountRangeSuccess && !hoursRecordedRangeSucess) || birdCountRangeFetching || hoursRecordedRangeFetching}
              isError={false} // Add error handling from hooks
              error={null} // Add error from hooks
              hasData={(birdCountRange && hoursRecordedRange && birdCountRange.length > 0) && (totalBirdsDetected ?? 0) > 0}
              onRetry={() => {
                // Refetch data
                window.location.reload();
              }}
            />
          </Box>

          {collisionRiskEnabled && (
            <Box id="bird-at-collision-risk-count">
              <BirdsAtRiskOfCollisionCount
                birdsAtRiskData={birdsAtCollisionRiskCountRange}
                xAxisOptions={xAxisRangeOptions}
                tickAmount={10}
                header="Count of birds in collision risk zone observed"
                subHeader="This graph shows the number of birds observed in the risk zone per day during the selected date range"
                isLoading={!birdsAtCollisionRiskCountRangeSuccess}
                isError={false} // Add error handling from hooks if needed
                error={null} // Add error from hooks if needed
                hasData={birdsAtCollisionRiskCountRange && birdsAtCollisionRiskCountRange.length > 0}
                onRetry={() => {
                  // Refetch data
                  window.location.reload();
                }}
              />
            </Box>
          )}
          
          <Box id="monthly-bird-count">
            <BirdCount
              barGraphBirdCount={birdCountPerHour}
              barGraphReliable={hoursRecordedPerHour}
              colors={colorPerHour}
              xAxisOptions={xAxisHourOptions}
              tickAmount={4}
              type="hour"
              header="Average number of birds per hour"
              subHeader="This graph shows the average number of birds observed per hour in the selected date range"
              isLoading={!birdCountPerHourSuccess || !hoursRecordedPerHourSuccess || birdCountPerHourFetching || hoursRecordedPerHourFetching}
              isError={false} // Add error handling from hooks if needed
              error={null} // Add error from hooks if needed
              hasData={birdCountPerHour && hoursRecordedPerHour && birdCountPerHour.length > 0 && birdCountPerHour.some((item: any) => item[1] > 0)}
              onRetry={() => {
                // Refetch data
                window.location.reload();
              }}
            />
          </Box>
          
          <Box id="observation-rate">
            <ObservationRate 
              data={birdObservationRateData}
              isLoading={!birdCountRangeSuccess || !hoursRecordedRangeSucess || birdCountRangeFetching || hoursRecordedRangeFetching}
              isError={false} // Add error handling from hooks if needed
              error={null} // Add error from hooks if needed
              hasData={birdObservationRateData && birdObservationRateData.length > 0}
              onRetry={() => {
                // Refetch data
                window.location.reload();
              }}
            />
          </Box>
          
          <Box id="direction-radar-chart">
            <DirectionRadarChart
              data={birdCountDirectionDateRange}
              id="direction-radar-chart"
              isLoading={!birdCountDirectionDateRangeSuccess}
              isError={false} // Add error handling from hooks
              error={null} // Add error from hooks
              hasData={birdCountDirectionDateRange && Object.keys(birdCountDirectionDateRange).length > 0}
              onRetry={() => {
                // Refetch data
                window.location.reload();
              }}
            />
          </Box>
          
          {heightGraphV1Enabled && (
            <Box id="bird-height-distribution">
              <BirdHeightDistribution 
                data={birdHeight?.data} 
                site={site}
                isLoading={birdHeightLoading}
                isError={birdHeightError}
                error={null}
                hasData={birdHeight?.data && Object.keys(birdHeight.data).length > 0}
                onRetry={() => {
                  // Refetch data
                  window.location.reload();
                }}
              />
            </Box>
          )}
          
          {heightGraphV2Enabled && (
            <Box id="bird-height-distribution-v2">
              <BirdHeightDistributionV2 
                data={birdHeightV2?.data}
                isLoading={birdHeightV2Loading}
                isError={birdHeightV2Error}
                error={null}
                hasData={birdHeightV2?.data && Object.keys(birdHeightV2.data).length > 0}
                onRetry={() => {
                  // Refetch data
                  window.location.reload();
                }}
              />
            </Box>
          )}
          
          {heightGraphV3Enabled && (
            <Box id="bird-height-distribution-v3">
              <BirdHeightDistributionV3 
                data={birdHeightV3?.data} 
                site={site}
                isLoading={birdHeightV3Loading}
                isError={birdHeightV3Error}
                error={null}
                hasData={birdHeightV3?.data && Object.keys(birdHeightV3.data).length > 0}
                onRetry={() => {
                  // Refetch data
                  window.location.reload();
                }}
              />
            </Box>
          )}
          
          <Box id="wind-speed-wrapper">
            <WindSpeedWrapper
              categories={windSpeedCategories}
              values={windSpeedValues}
              isLoading={!windSpeedDateRangeSuccess}
              isError={false} // Add error handling from hooks if needed
              error={null} // Add error from hooks if needed
              hasData={windSpeedCategories && windSpeedValues && windSpeedCategories.length > 0}
              onRetry={() => {
                // Refetch data
                window.location.reload();
              }}
            />
          </Box>
          
          <Box id="wind-direction-radar-chart">
            <WindDirectionRadarChart
              data={birdCountWindDirection}
              id="wind-direction-radar-chart"
              isLoading={!birdCountWindDirectionSuccess}
              isError={false} // Add error handling from hooks
              error={null} // Add error from hooks
              hasData={birdCountWindDirection && Object.keys(birdCountWindDirection).length > 0}
              onRetry={() => {
                // Refetch data
                window.location.reload();
              }}
            />
          </Box>
          
          {speciesPercentageDateRange && speciesPercentageDateRange.length > 0 && (
            <Box id="species-data-wrapper">
              <SpeciesData
                rows={speciesPercentageDateRange}
                totalBirdsDetected={totalBirdsDetected}
                totalSpeciesDetected={totalSpeciesDetected}
                isLoading={!speciesDateRangeSuccess}
                isError={false} // Add error handling from hooks if needed
                error={null} // Add error from hooks if needed
                hasData={speciesPercentageDateRange && speciesPercentageDateRange.length > 0}
                onRetry={() => {
                  // Refetch data
                  window.location.reload();
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* <section className="radar-chart-wrapper" id="radar-chart-wrapper">
        <RadarChartWrapper />
      </section>      
      <section id="species-distribution-monthly">
        <SpeciesDistributionMonthly />
      </section> */}
    </Box>
  );
}
